import React, { useState, useEffect } from 'react';
import {
    Table, Card, Button, Input, Space, Modal, Form,
    message, Tag, Dropdown, Typography, Select, Popconfirm, InputNumber,
    Switch, Tabs, Badge, Collapse, Tree, Tooltip, Divider
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    UserAddOutlined,
    SwapOutlined,
    DeleteOutlined,
    MoreOutlined,
    DollarOutlined,
    TeamOutlined,
    BranchesOutlined,
    ReloadOutlined,
    DownloadOutlined,
    UploadOutlined,
    StopOutlined,
    PlayCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import agentService from '../../services/agent.service';
import creditService from '../../services/credit.service';
import { useAuth } from '../../contexts/AuthContext';
import PermissionWrapper from '../../components/PermissionWrapper';
import { usePermissions } from '../../hooks/usePermissions';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const Members = () => {
    const { user } = useAuth(); // 添加當前用戶資訊
    const { canWrite } = usePermissions();
    const [loading, setLoading] = useState(false);
    const [members, setMembers] = useState([]);
    const [allMembers, setAllMembers] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });
    const [searchText, setSearchText] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [activeTab, setActiveTab] = useState('direct');
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'tree'
    const [agentFilter, setAgentFilter] = useState('');

    // Modal states
    const [addMemberModalVisible, setAddMemberModalVisible] = useState(false);
    const [transferModalVisible, setTransferModalVisible] = useState(false);
    const [depositModalVisible, setDepositModalVisible] = useState(false);
    const [withdrawModalVisible, setWithdrawModalVisible] = useState(false);
    const [batchActionModalVisible, setBatchActionModalVisible] = useState(false);
    const [removeMemberModalVisible, setRemoveMemberModalVisible] = useState(false);
    const [bettingStatusModalVisible, setBettingStatusModalVisible] = useState(false);
    const [batchBettingStatusModalVisible, setBatchBettingStatusModalVisible] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [subAgents, setSubAgents] = useState([]);
    const [allAgents, setAllAgents] = useState([]);
    const [stats, setStats] = useState({});

    const [form] = Form.useForm();
    const [transferForm] = Form.useForm();
    const [depositForm] = Form.useForm();
    const [withdrawForm] = Form.useForm();
    const [batchForm] = Form.useForm();
    const [removeMemberForm] = Form.useForm();
    const [bettingStatusForm] = Form.useForm();
    const [batchBettingStatusForm] = Form.useForm();

    useEffect(() => {
        if (activeTab === 'direct') {
            loadDirectMembers();
        } else {
            loadAllMembers();
        }
        loadSubAgents();
    }, [pagination.current, searchText, activeTab, agentFilter]);

    const loadDirectMembers = async () => {
        try {
            setLoading(true);
            const response = await agentService.getMembers({
                page: pagination.current,
                limit: pagination.pageSize,
                search: searchText,
                directOnly: 'true'
            });

            if (response && response.data) {
                setMembers(response.data);
                setPagination({
                    ...pagination,
                    total: response.pagination?.total || 0
                });
            } else if (response && Array.isArray(response.members)) {
                setMembers(response.members);
                setPagination({
                    ...pagination,
                    total: response.pagination?.total || 0
                });
            } else {
                console.error('Invalid members response:', response);
                setMembers([]);
            }
        } catch (error) {
            console.error('Direct Members API Error:', error);
            message.error('載入直屬會員失敗');
            setMembers([]);
        } finally {
            setLoading(false);
        }
    };

    const loadAllMembers = async () => {
        try {
            setLoading(true);
            const response = await agentService.getAllMembers({
                page: pagination.current,
                limit: pagination.pageSize,
                search: searchText,
                agentFilter,
                groupByAgent: viewMode === 'tree' ? 'true' : 'false'
            });

            if (response && response.data) {
                setAllMembers(response.data);
                setAllAgents(response.agents || []);
                setStats(response.stats || {});
                setPagination({
                    ...pagination,
                    total: response.pagination?.total || 0
                });
            } else if (response && Array.isArray(response.members)) {
                setAllMembers(response.members);
                setAllAgents(response.agents || []);
                setStats(response.stats || {});
                setPagination({
                    ...pagination,
                    total: response.pagination?.total || 0
                });
            } else {
                console.error('Invalid all members response:', response);
                setAllMembers([]);
                setAllAgents([]);
                setStats({});
            }
        } catch (error) {
            console.error('All Members API Error:', error);
            message.error('載入所有會員失敗');
            setAllMembers([]);
            setAllAgents([]);
            setStats({});
        } finally {
            setLoading(false);
        }
    };

    const loadSubAgents = async () => {
        try {
            const response = await agentService.getSubAgents({
                page: 1,
                limit: 100,
                status: 'active'
            });

            if (response && response.data) {
                setSubAgents(response.data);
            } else if (response && Array.isArray(response.agents)) {
                setSubAgents(response.agents);
            } else {
                console.error('Invalid sub agents response:', response);
                setSubAgents([]);
            }
        } catch (error) {
            console.error('載入下級代理失敗:', error);
            setSubAgents([]);
        }
    };

    const handleAddMember = async (values) => {
        try {
            const response = await agentService.createMember(values.userId);
            if (response.success) {
                message.success('會員新增成功');
                setAddMemberModalVisible(false);
                form.resetFields();
                activeTab === 'direct' ? loadDirectMembers() : loadAllMembers();
            }
        } catch (error) {
            message.error(error.response?.data?.error || '新增失敗');
        }
    };

    const handleTransferMember = async (values) => {
        try {
            const response = await agentService.transferMember({
                userId: selectedMember.userId,
                toAgentId: values.toAgentId,
                reason: values.reason
            });

            if (response.success) {
                message.success('會員轉移成功');
                setTransferModalVisible(false);
                transferForm.resetFields();
                activeTab === 'direct' ? loadDirectMembers() : loadAllMembers();
            }
        } catch (error) {
            console.error('轉移會員錯誤:', error);

            // 處理不同類型的錯誤
            let alertMessage = '❌ 轉移失敗\n\n';

            if (error.response?.data?.error) {
                alertMessage += error.response.data.error;

                // 如果後端返回了詳細資訊（例如餘額問題），顯示更詳細的錯誤
                if (error.response.data.details) {
                    const details = error.response.data.details;

                    alertMessage += '\n\n';
                    if (details.displayName) {
                        alertMessage += `會員：${details.displayName}\n`;
                    }
                    if (details.currentBalance !== undefined) {
                        alertMessage += `當前餘額：${details.currentBalance}\n`;
                    }
                    if (details.instruction) {
                        alertMessage += `\n處理方式：${details.instruction}`;
                    }
                }
            } else if (error.message) {
                alertMessage += error.message;
            } else {
                alertMessage += '發生未知錯誤';
            }

            window.alert(alertMessage);
        }
    };

    const handleBatchAction = async (values) => {
        try {
            let response;

            // 檢查是否為投注狀態相關操作
            if (values.action === 'disable-betting' || values.action === 'enable-betting') {
                const bettingStatus = values.action === 'disable-betting' ? 'disabled' : 'enabled';
                response = await agentService.batchSetBettingStatus({
                    userIds: selectedRowKeys,
                    bettingStatus,
                    reason: values.reason || ''
                });
            } else {
                response = await agentService.batchManageMembers({
                    action: values.action,
                    userIds: selectedRowKeys,
                    targetAgentId: values.targetAgentId,
                    reason: values.reason
                });
            }

            if (response.success) {
                message.success(response.message);
                setBatchActionModalVisible(false);
                batchForm.resetFields();
                setSelectedRowKeys([]);
                activeTab === 'direct' ? loadDirectMembers() : loadAllMembers();
            }
        } catch (error) {
            // 使用 alert 顯示詳細錯誤訊息
            let alertMessage = '❌ 批量操作失敗\n\n';

            if (error.response?.data?.error) {
                alertMessage += error.response.data.error;

                // 如果有詳細信息（如餘額不為0的會員列表），也一起顯示
                if (error.response.data.details) {
                    alertMessage += '\n\n詳細資訊：\n' + JSON.stringify(error.response.data.details, null, 2);
                }
            } else if (error.message) {
                alertMessage += error.message;
            } else {
                alertMessage += '發生未知錯誤';
            }

            window.alert(alertMessage);
        }
    };

    const handleRemoveMember = async (values) => {
        try {
            const userId = selectedMember.userId;
            const memberName = selectedMember.displayName;
            const reason = values?.reason || '管理員移除';

            console.log('開始移除會員:', { userId, memberName, reason });

            const response = await agentService.removeMember(userId, reason);

            console.log('移除會員響應:', response);

            if (response && response.success) {
                let successContent = `已成功移除會員 "${memberName || userId}"`;

                message.success({
                    content: successContent,
                    duration: 5 // 延長顯示時間
                });

                // 如果有警告信息，也顯示給用戶
                if (response.warnings && response.warnings.length > 0) {
                    setTimeout(() => {
                        message.info({
                            content: '操作提醒：' + response.warnings.join('；'),
                            duration: 4
                        });
                    }, 2000);
                }

                // 關閉Modal並重置表單
                setRemoveMemberModalVisible(false);
                removeMemberForm.resetFields();

                // 刷新列表
                if (activeTab === 'direct') {
                    await loadDirectMembers();
                } else {
                    await loadAllMembers();
                }

                console.log('會員列表已刷新');
            } else {
                throw new Error(response?.message || '移除失敗');
            }
        } catch (error) {
            console.error('移除會員錯誤:', {
                selectedMember: selectedMember?.userId,
                error: error.message,
                response: error.response?.data
            });

            // 處理不同類型的錯誤
            let alertMessage = '❌ 移除失敗\n\n';

            if (error.response?.status === 403) {
                alertMessage += '權限不足：該會員不屬於您的線下';
            } else if (error.response?.status === 404) {
                alertMessage += '找不到該會員';
            } else if (error.response?.data?.error) {
                alertMessage += error.response.data.error;

                // 如果後端返回了詳細資訊（例如餘額問題），顯示更詳細的錯誤
                if (error.response.data.details) {
                    const details = error.response.data.details;

                    alertMessage += '\n\n';
                    if (details.displayName) {
                        alertMessage += `會員：${details.displayName}\n`;
                    }
                    if (details.currentBalance !== undefined) {
                        alertMessage += `當前餘額：${details.currentBalance}\n`;
                    }
                    if (details.instruction) {
                        alertMessage += `\n處理方式：${details.instruction}`;
                    }
                }
            } else if (error.message) {
                alertMessage += error.message;
            } else {
                alertMessage += '發生未知錯誤';
            }

            window.alert(alertMessage);
        }
    };

    const handleDeposit = async (values) => {
        try {
            const response = await creditService.depositToMember({
                userId: selectedMember.userId,
                amount: values.amount,
                note: values.note
            });

            if (response.success) {
                message.success('充值成功');
                setDepositModalVisible(false);
                depositForm.resetFields();
                activeTab === 'direct' ? loadDirectMembers() : loadAllMembers();
            }
        } catch (error) {
            // 檢查是否為跨級充值錯誤
            if (error.response?.data?.details?.isCrossLevel) {
                const details = error.response.data.details;
                window.alert(
                    `❌ 充值失敗\n\n` +
                    `${error.response.data.error}\n\n` +
                    `該會員屬於：${details.targetAgentName}\n` +
                    `可用額度：${details.availableCredit.toLocaleString()}\n` +
                    `請求金額：${details.requestedAmount.toLocaleString()}\n\n` +
                    `處理方式：\n` +
                    details.instruction
                );
            } else {
                message.error(error.response?.data?.error || '充值失敗');
            }
        }
    };

    const handleWithdraw = async (values) => {
        try {
            const response = await creditService.withdrawFromMember({
                userId: selectedMember.userId,
                amount: values.amount,
                note: values.note
            });

            if (response.success) {
                message.success('提領成功');
                setWithdrawModalVisible(false);
                withdrawForm.resetFields();
                activeTab === 'direct' ? loadDirectMembers() : loadAllMembers();
            }
        } catch (error) {
            // 提領一般不會有跨級額度問題（不需要檢查額度），所以直接顯示錯誤
            message.error(error.response?.data?.error || '提領失敗');
        }
    };

    const handleBettingStatus = async (values) => {
        try {
            const response = await agentService.setMemberBettingStatus({
                userId: selectedMember.userId,
                bettingStatus: values.bettingStatus,
                reason: values.reason || ''
            });

            if (response.success) {
                const statusText = values.bettingStatus === 'enabled' ? '恢復投注' : '禁止投注';
                message.success(`已成功設定會員${statusText}`);
                setBettingStatusModalVisible(false);
                bettingStatusForm.resetFields();
                activeTab === 'direct' ? loadDirectMembers() : loadAllMembers();
            }
        } catch (error) {
            message.error(error.response?.data?.error || '設定失敗');
        }
    };

    const handleBatchBettingStatus = async (values) => {
        try {
            const response = await agentService.batchSetBettingStatus({
                userIds: selectedRowKeys,
                bettingStatus: values.bettingStatus,
                reason: values.reason || ''
            });

            if (response.success) {
                message.success(response.message);
                setBatchBettingStatusModalVisible(false);
                batchBettingStatusForm.resetFields();
                setSelectedRowKeys([]);
                activeTab === 'direct' ? loadDirectMembers() : loadAllMembers();
            }
        } catch (error) {
            message.error(error.response?.data?.error || '批量設定失敗');
        }
    };


    const getActionMenuItems = (record) => {
        console.log('Creating action menu for record:', record); // 添加調試日誌

        // 觀察者只能查看，不能進行任何操作
        if (!canWrite()) {
            return [];
        }

        const actions = [
            {
                key: 'deposit',
                icon: <DollarOutlined />,
                label: '充值',
                onClick: (menuInfo) => {
                    console.log('Deposit clicked for:', record.userId);
                    setSelectedMember(record);
                    setDepositModalVisible(true);
                }
            },
            {
                key: 'withdraw',
                icon: <DownloadOutlined />,
                label: '提領',
                onClick: (menuInfo) => {
                    console.log('Withdraw clicked for:', record.userId);
                    setSelectedMember(record);
                    setWithdrawModalVisible(true);
                }
            },
            {
                type: 'divider'
            }
        ];

        // 添加投注狀態控制
        if (record.bettingStatus === 'disabled') {
            actions.push({
                key: 'enable-betting',
                icon: <PlayCircleOutlined />,
                label: '恢復投注',
                onClick: (menuInfo) => {
                    setSelectedMember(record);
                    bettingStatusForm.setFieldsValue({
                        bettingStatus: 'enabled',
                        reason: ''
                    });
                    setBettingStatusModalVisible(true);
                }
            });
        } else {
            actions.push({
                key: 'disable-betting',
                icon: <StopOutlined />,
                label: '禁止投注',
                onClick: (menuInfo) => {
                    setSelectedMember(record);
                    bettingStatusForm.setFieldsValue({
                        bettingStatus: 'disabled',
                        reason: ''
                    });
                    setBettingStatusModalVisible(true);
                }
            });
        }

        actions.push(
            {
                key: 'transfer',
                icon: <SwapOutlined />,
                label: '轉移',
                onClick: (menuInfo) => {
                    console.log('Transfer clicked for:', record.userId);
                    setSelectedMember(record);
                    setTransferModalVisible(true);
                }
            },
            {
                type: 'divider'
            },
            {
                key: 'remove',
                icon: <DeleteOutlined />,
                label: '移除',
                danger: true,
                onClick: (menuInfo) => {
                    console.log('點擊移除按鈕:', { userId: record.userId, displayName: record.displayName });
                    setSelectedMember(record);
                    setRemoveMemberModalVisible(true);
                }
            }
        );

        return actions;
    };

    const directMembersColumns = [
        {
            title: 'ID',
            dataIndex: 'userId',
            key: 'userId',
            width: window.innerWidth <= 768 ? 100 : 150,
            fixed: window.innerWidth <= 768 ? false : 'left'
        },
        {
            title: '顯示名稱',
            dataIndex: 'displayName',
            key: 'displayName',
        },
        {
            title: '餘額',
            key: 'balance',
            dataIndex: ['wallet', 'balance'],
        },
        {
            title: '總投注',
            dataIndex: 'totalBets',
            key: 'totalBets',
            render: (count) => `${count || 0} 筆`,
            sorter: (a, b) => (a.totalBets || 0) - (b.totalBets || 0)
        },
        {
            title: '最後活動',
            dataIndex: 'lastActivity',
            key: 'lastActivity',
            render: (date) => date ? new Date(date).toLocaleString('zh-TW') : '-',
            sorter: (a, b) => new Date(a.lastActivity || 0) - new Date(b.lastActivity || 0)
        },
        {
            title: '帳戶狀態',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status === 'active' ? '正常' : '停用'}
                </Tag>
            ),
        },
        {
            title: '投注狀態',
            dataIndex: 'bettingStatus',
            key: 'bettingStatus',
            render: (bettingStatus, record) => {
                if (bettingStatus === 'disabled') {
                    return (
                        <Tooltip
                            title={
                                <div>
                                    <div>原因: {record.bettingDisabledReason || '無'}</div>
                                    <div>時間: {record.bettingDisabledAt ? new Date(record.bettingDisabledAt).toLocaleString('zh-TW') : '無'}</div>
                                </div>
                            }
                        >
                            <Tag color="red" icon={<StopOutlined />}>
                                禁止投注
                            </Tag>
                        </Tooltip>
                    );
                }
                return (
                    <Tag color="green" icon={<PlayCircleOutlined />}>
                        允許投注
                    </Tag>
                );
            },
            filters: [
                { text: '允許投注', value: 'enabled' },
                { text: '禁止投注', value: 'disabled' }
            ],
            onFilter: (value, record) => record.bettingStatus === value,
        },
        {
            title: '操作',
            key: 'action',
            fixed: window.innerWidth <= 768 ? false : 'right',
            width: window.innerWidth <= 768 ? 80 : 100,
            render: (_, record) => {
                console.log('Rendering actions for record:', record.userId);
                const menuItems = getActionMenuItems(record);
                console.log('Menu items:', menuItems);
                return (
                    <Space>
                        <Dropdown
                            menu={{ items: menuItems }}
                            placement="bottomRight"
                            trigger={['click']}
                        >
                            <Button
                                type="text"
                                icon={<MoreOutlined />}
                                size={window.innerWidth <= 768 ? "small" : "middle"}
                            />
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    const allMembersColumns = [
        {
            title: 'ID',
            dataIndex: 'userId',
            key: 'userId',
            width: window.innerWidth <= 768 ? 100 : 150,
            fixed: window.innerWidth <= 768 ? false : 'left'
        },
        {
            title: '顯示名稱',
            dataIndex: 'displayName',
            key: 'displayName',
        },
        {
            title: '所屬代理',
            key: 'agent',
            render: (_, record) => {
                if (!record.agent) return <Text type="secondary">未分配</Text>;

                return (
                    <Space direction="vertical" size={0}>
                        <Text strong>{record.agent.displayName}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Level {record.agent.level} | {record.isDirect ? '直屬' : '間接'}
                        </Text>
                    </Space>
                );
            }
        },
        {
            title: '代理路徑',
            key: 'agentPath',
            render: (_, record) => {
                if (!record.agentPath || record.agentPath.length === 0) {
                    return record.isDirect ? <Tag color="blue">直屬會員</Tag> : '-';
                }

                return (
                    <Tooltip title={record.agentPath.map(p => `${p.name} (L${p.level})`).join(' → ')}>
                        <Text style={{ fontSize: '12px' }}>
                            {record.agentPath.map(p => p.name).join(' → ')}
                        </Text>
                    </Tooltip>
                );
            }
        },
        {
            title: '餘額',
            key: 'balance',
            dataIndex: ['wallet', 'balance'],
        },
        {
            title: '總投注',
            dataIndex: 'totalBets',
            key: 'totalBets',
            render: (count) => `${count || 0} 筆`,
            sorter: (a, b) => (a.totalBets || 0) - (b.totalBets || 0)
        },
        {
            title: '最後活動',
            dataIndex: 'lastActivity',
            key: 'lastActivity',
            render: (date) => date ? new Date(date).toLocaleString('zh-TW') : '-',
            sorter: (a, b) => new Date(a.lastActivity || 0) - new Date(b.lastActivity || 0)
        },
        {
            title: '投注狀態',
            dataIndex: 'bettingStatus',
            key: 'bettingStatus',
            render: (bettingStatus, record) => {
                if (bettingStatus === 'disabled') {
                    return (
                        <Tooltip
                            title={
                                <div>
                                    <div>原因: {record.bettingDisabledReason || '無'}</div>
                                    <div>時間: {record.bettingDisabledAt ? new Date(record.bettingDisabledAt).toLocaleString('zh-TW') : '無'}</div>
                                </div>
                            }
                        >
                            <Tag color="red" icon={<StopOutlined />}>
                                禁止投注
                            </Tag>
                        </Tooltip>
                    );
                }
                return (
                    <Tag color="green" icon={<PlayCircleOutlined />}>
                        允許投注
                    </Tag>
                );
            },
            filters: [
                { text: '允許投注', value: 'enabled' },
                { text: '禁止投注', value: 'disabled' }
            ],
            onFilter: (value, record) => record.bettingStatus === value,
        },
        {
            title: '操作',
            key: 'action',
            fixed: window.innerWidth <= 768 ? false : 'right',
            width: window.innerWidth <= 768 ? 80 : 100,
            render: (_, record) => {
                console.log('Rendering actions for all members record:', record.userId); // 添加調試日誌
                const menuItems = getActionMenuItems(record);
                return (
                    <Space>
                        <Dropdown
                            menu={{
                                items: menuItems,
                                onClick: (menuInfo) => {
                                    console.log('菜單項目被點擊:', menuInfo.key, '會員:', record.userId);
                                }
                            }}
                            placement="bottomRight"
                            trigger={['click']}
                        >
                            <Button
                                type="text"
                                icon={<MoreOutlined />}
                                title="更多操作"
                                size={window.innerWidth <= 768 ? "small" : "middle"}
                            />
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
        onSelect: (record, selected, selectedRows) => {
            // 可以在這裡添加選擇邏輯
        },
        onSelectAll: (selected, selectedRows, changeRows) => {
            // 可以在這裡添加全選邏輯
        }
    };

    const getBatchActionOptions = () => [
        { value: 'transfer', label: '轉移給其他代理' },
        { value: 'recall', label: '回收到我的線下' },
        { value: 'remove', label: '解除關聯' },
        { value: 'disable-betting', label: '批量禁止投注' },
        { value: 'enable-betting', label: '批量恢復投注' }
    ];

    // 獲取去重後的代理列表（包含自己）
    const getUniqueAgentOptions = () => {
        const agents = [];
        const seenIds = new Set();

        // 添加自己為選項
        if (user && !seenIds.has(user.id)) {
            agents.push({
                id: user.id,
                displayName: user.displayName || user.username,
                level: user.level,
                isSelf: true
            });
            seenIds.add(user.id);
        }

        // 添加下級代理
        (subAgents || []).forEach(agent => {
            if (!seenIds.has(agent.id)) {
                agents.push({ ...agent, isSelf: false });
                seenIds.add(agent.id);
            }
        });

        // 添加所有代理
        (allAgents || []).forEach(agent => {
            if (!seenIds.has(agent.id)) {
                agents.push({ ...agent, isSelf: false });
                seenIds.add(agent.id);
            }
        });

        return agents;
    };

    return (
        <div>
            <Title level={2}>會員管理</Title>

            <Card>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    tabBarExtraContent={
                        <Space wrap={window.innerWidth <= 768}>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => activeTab === 'direct' ? loadDirectMembers() : loadAllMembers()}
                                size={window.innerWidth <= 768 ? "small" : "middle"}
                            >
                                重新載入
                            </Button>
                            <PermissionWrapper requireWrite={true}>
                                <Button
                                    type="primary"
                                    icon={<UserAddOutlined />}
                                    onClick={() => setAddMemberModalVisible(true)}
                                    size={window.innerWidth <= 768 ? "small" : "middle"}
                                >
                                    新增會員
                                </Button>
                            </PermissionWrapper>
                        </Space>
                    }
                    type={window.innerWidth <= 768 ? "line" : "card"}
                    items={[
                        {
                            key: 'direct',
                            label: <span><UserAddOutlined />直屬會員</span>,
                            children: (
                                <>
                                    <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                                        <Space style={{ width: window.innerWidth <= 768 ? '100%' : 'auto' }}>
                                            <Search
                                                placeholder="搜尋會員ID或名稱"
                                                allowClear
                                                onSearch={setSearchText}
                                                style={{ width: window.innerWidth <= 768 ? '100%' : 300 }}
                                            />
                                        </Space>

                                        <Space>
                                            {selectedRowKeys.length > 0 && (
                                                <>
                                                    <Text type="secondary">已選擇 {selectedRowKeys.length} 個會員</Text>
                                                    <PermissionWrapper requireWrite={true}>
                                                        <Button
                                                            type="primary"
                                                            ghost
                                                            onClick={() => setBatchActionModalVisible(true)}
                                                        >
                                                            批量操作
                                                        </Button>
                                                    </PermissionWrapper>
                                                </>
                                            )}
                                        </Space>
                                    </Space>

                                    <Table
                                        rowKey="userId"
                                        columns={directMembersColumns}
                                        dataSource={members}
                                        loading={loading}
                                        pagination={{
                                            ...pagination,
                                            showSizeChanger: true,
                                            showTotal: (total) => `共 ${total} 筆`,
                                            onChange: (page, pageSize) => {
                                                setPagination({ ...pagination, current: page, pageSize });
                                            }
                                        }}
                                        rowSelection={rowSelection}
                                        scroll={{ x: window.innerWidth <= 768 ? 800 : 1200 }}
                                        size={window.innerWidth <= 768 ? "small" : "middle"}
                                    />
                                </>
                            )
                        },
                        {
                            key: 'all',
                            label: (
                                <span>
                                    <TeamOutlined />
                                    所有會員
                                    {stats.totalMembers && <Badge count={stats.totalMembers} style={{ marginLeft: 4 }} />}
                                </span>
                            ),
                            children: (
                                <>
                                    {/* 統計資訊 */}
                                    {stats.totalMembers && (
                                        <Card size="small" style={{ marginBottom: 16 }}>
                                            <Space size="large">
                                                <Text>總會員數: <Text strong>{stats.totalMembers}</Text></Text>
                                                <Text>直屬會員: <Text strong>{stats.directMembers}</Text></Text>
                                                <Text>下級代理數: <Text strong>{stats.totalAgents}</Text></Text>
                                            </Space>
                                        </Card>
                                    )}

                                    <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                                        <Space wrap={window.innerWidth <= 768} style={{ width: window.innerWidth <= 768 ? '100%' : 'auto' }}>
                                            <Search
                                                placeholder="搜尋會員ID或名稱"
                                                allowClear
                                                onSearch={setSearchText}
                                                style={{ width: window.innerWidth <= 768 ? '100%' : 300 }}
                                            />
                                            <Select
                                                placeholder="篩選代理"
                                                allowClear
                                                style={{ width: window.innerWidth <= 768 ? '100%' : 200 }}
                                                value={agentFilter}
                                                onChange={setAgentFilter}
                                            >
                                                {allAgents.map(agent => (
                                                    <Option key={agent.id} value={agent.id}>
                                                        {agent.displayName} (L{agent.level})
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Space>

                                        <Space>
                                            {selectedRowKeys.length > 0 && (
                                                <>
                                                    <Text type="secondary">已選擇 {selectedRowKeys.length} 個會員</Text>
                                                    <PermissionWrapper requireWrite={true}>
                                                        <Button
                                                            type="primary"
                                                            ghost
                                                            onClick={() => setBatchActionModalVisible(true)}
                                                        >
                                                            批量操作
                                                        </Button>
                                                    </PermissionWrapper>
                                                </>
                                            )}
                                        </Space>
                                    </Space>

                                    <Table
                                        rowKey="userId"
                                        columns={allMembersColumns}
                                        dataSource={allMembers}
                                        loading={loading}
                                        pagination={{
                                            ...pagination,
                                            showSizeChanger: true,
                                            showTotal: (total) => `共 ${total} 筆`,
                                            onChange: (page, pageSize) => {
                                                setPagination({ ...pagination, current: page, pageSize });
                                            }
                                        }}
                                        rowSelection={rowSelection}
                                        scroll={{ x: window.innerWidth <= 768 ? 1000 : 1400 }}
                                        size={window.innerWidth <= 768 ? "small" : "middle"}
                                    />
                                </>
                            )
                        }
                    ]}
                />
            </Card>

            {/* 新增會員 Modal */}
            <Modal
                title="新增會員"
                open={addMemberModalVisible}
                onCancel={() => {
                    setAddMemberModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
            >
                <Form form={form} onFinish={handleAddMember} layout="vertical">
                    <Form.Item
                        name="userId"
                        label="會員 LINE ID"
                        rules={[{ required: true, message: '請輸入會員 LINE ID' }]}
                    >
                        <Input placeholder="請輸入會員的 LINE ID" />
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setAddMemberModalVisible(false)}>
                                取消
                            </Button>
                            <Button type="primary" htmlType="submit">
                                確認新增
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 轉移會員 Modal */}
            <Modal
                title="轉移會員"
                open={transferModalVisible}
                onCancel={() => {
                    setTransferModalVisible(false);
                    transferForm.resetFields();
                }}
                footer={null}
            >
                <Form form={transferForm} onFinish={handleTransferMember} layout="vertical">
                    <Form.Item label="會員資訊">
                        <Input value={`${selectedMember?.displayName} (${selectedMember?.userId})`} disabled />
                    </Form.Item>

                    {selectedMember?.agent && (
                        <Form.Item label="當前代理">
                            <Input
                                value={`${selectedMember.agent.displayName} (Level ${selectedMember.agent.level})`}
                                disabled
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="toAgentId"
                        label="轉移至"
                        rules={[{ required: true, message: '請選擇目標代理' }]}
                    >
                        <Select placeholder="請選擇目標代理">
                            {getUniqueAgentOptions().map(agent => (
                                <Option key={`transfer-${agent.id}`} value={agent.id}>
                                    {agent.isSelf ? `我自己 (${agent.displayName})` : `${agent.displayName} (Level ${agent.level})`}
                                    {agent.isSelf && <Tag color="blue" style={{ marginLeft: 8 }}>自己</Tag>}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="reason"
                        label="轉移原因"
                    >
                        <Input.TextArea rows={3} placeholder="請輸入轉移原因（選填）" />
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setTransferModalVisible(false)}>
                                取消
                            </Button>
                            <Button type="primary" htmlType="submit">
                                確認轉移
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 批量操作 Modal */}
            <Modal
                title={`批量操作 (${selectedRowKeys.length} 個會員)`}
                open={batchActionModalVisible}
                onCancel={() => {
                    setBatchActionModalVisible(false);
                    batchForm.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form form={batchForm} onFinish={handleBatchAction} layout="vertical">
                    <Form.Item
                        name="action"
                        label="操作類型"
                        rules={[{ required: true, message: '請選擇操作類型' }]}
                    >
                        <Select placeholder="請選擇操作類型">
                            {getBatchActionOptions().map(option => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.action !== currentValues.action
                        }
                    >
                        {({ getFieldValue }) => {
                            const action = getFieldValue('action');
                            if (action === 'transfer') {
                                return (
                                    <Form.Item
                                        name="targetAgentId"
                                        label="目標代理"
                                        rules={[{ required: true, message: '請選擇目標代理' }]}
                                    >
                                        <Select placeholder="請選擇目標代理">
                                            {getUniqueAgentOptions().map(agent => (
                                                <Option key={`batch-${agent.id}`} value={agent.id}>
                                                    {agent.isSelf ? `我自己 (${agent.displayName})` : `${agent.displayName} (Level ${agent.level})`}
                                                    {agent.isSelf && <Tag color="blue" style={{ marginLeft: 8 }}>自己</Tag>}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                );
                            }

                            if (action === 'disable-betting') {
                                return (
                                    <div style={{
                                        background: '#fff2f0',
                                        border: '1px solid #ffccc7',
                                        borderRadius: 6,
                                        padding: 12,
                                        marginBottom: 16
                                    }}>
                                        <Text strong style={{ color: '#ff4d4f' }}>
                                            <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                                            批量禁止投注
                                        </Text>
                                        <div style={{ marginTop: 8, fontSize: '14px', color: '#666' }}>
                                            將對選中的 {selectedRowKeys.length} 名會員禁止投注，他們將無法進行任何投注操作。
                                        </div>
                                    </div>
                                );
                            }

                            if (action === 'enable-betting') {
                                return (
                                    <div style={{
                                        background: '#f6ffed',
                                        border: '1px solid #b7eb8f',
                                        borderRadius: 6,
                                        padding: 12,
                                        marginBottom: 16
                                    }}>
                                        <Text strong style={{ color: '#52c41a' }}>
                                            <PlayCircleOutlined style={{ marginRight: 4 }} />
                                            批量恢復投注
                                        </Text>
                                        <div style={{ marginTop: 8, fontSize: '14px', color: '#666' }}>
                                            將對選中的 {selectedRowKeys.length} 名會員恢復投注權限。
                                        </div>
                                    </div>
                                );
                            }

                            return null;
                        }}
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.action !== currentValues.action
                        }
                    >
                        {({ getFieldValue }) => {
                            const action = getFieldValue('action');
                            const isDisableBetting = action === 'disable-betting';
                            const isEnableBetting = action === 'enable-betting';
                            const isBettingAction = isDisableBetting || isEnableBetting;

                            return (
                                <Form.Item
                                    name="reason"
                                    label={isBettingAction ? "操作原因" : "操作原因"}
                                    rules={[]}
                                >
                                    <Input.TextArea
                                        rows={3}
                                        placeholder={
                                            isDisableBetting ? "請輸入禁止投注的原因（選填）..." :
                                            isEnableBetting ? "請輸入恢復投注的原因（選填）..." :
                                            "請輸入操作原因（選填）..."
                                        }
                                        maxLength={200}
                                        showCount={isBettingAction}
                                    />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setBatchActionModalVisible(false)}>
                                取消
                            </Button>
                            <Button type="primary" htmlType="submit">
                                確認操作
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 充值 Modal */}
            <Modal
                title="會員充值"
                open={depositModalVisible}
                onCancel={() => {
                    setDepositModalVisible(false);
                    depositForm.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form form={depositForm} onFinish={handleDeposit} layout="vertical">
                    <Form.Item label="會員資訊">
                        <Input
                            value={`${selectedMember?.displayName} (${selectedMember?.userId})`}
                            disabled
                        />
                    </Form.Item>

                    <Form.Item label="當前餘額">
                        <Input
                            value={`${(selectedMember?.wallet?.balance || 0).toLocaleString()}`}
                            disabled
                        />
                    </Form.Item>

                    {/* 新增：代理額度資訊 */}
                    {selectedMember?.agent && selectedMember.agent.level !== 0 && (
                        <Form.Item label={selectedMember?.isDirect ? "您的額度狀況" : "會員所屬代理額度"}>
                            <div style={{ background: '#e6f7ff', padding: '12px', borderRadius: '6px', border: '1px solid #91d5ff' }}>
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Text>{selectedMember?.isDirect ? "您的額度" : "代理"}：<Text strong>{selectedMember.agent.displayName || selectedMember.agent.username}</Text></Text>
                                    <Text>額度上限：<Text strong>${(selectedMember.agent.creditLimit || 0).toLocaleString()}</Text></Text>
                                    <Text>已用額度：<Text type="danger">${(selectedMember.agent.usedCredit || 0).toLocaleString()}</Text></Text>
                                    <Text>可用額度：<Text strong style={{ color: '#52c41a' }}>${((selectedMember.agent.creditLimit || 0) - (selectedMember.agent.usedCredit || 0)).toLocaleString()}</Text></Text>
                                    <Divider style={{ margin: '8px 0' }} />
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        💡 充值後，{selectedMember?.isDirect ? "您的" : "該代理"}已用額度會增加
                                    </Text>
                                </Space>
                            </div>
                        </Form.Item>
                    )}

                    <Form.Item
                        name="amount"
                        label="充值金額"
                        rules={[
                            { required: true, message: '請輸入充值金額' },
                            { type: 'number', min: 1, message: '金額必須大於0' },
                            // 新增：前端預先檢查可用額度
                            {
                                validator: (_, value) => {
                                    // superadmin 或無效值時跳過檢查
                                    if (!value || !selectedMember?.agent || selectedMember.agent.level === 0) {
                                        return Promise.resolve();
                                    }
                                    // 檢查會員所屬代理的額度（直屬或跨級都檢查）
                                    const availableCredit = (selectedMember.agent.creditLimit || 0) - (selectedMember.agent.usedCredit || 0);
                                    if (value > availableCredit) {
                                        const errorMsg = selectedMember?.isDirect
                                            ? `❌ 充值金額超過您的可用額度！\n您的可用額度：${availableCredit.toLocaleString()}\n您輸入：${value.toLocaleString()}`
                                            : `❌ 充值金額超過代理可用額度！\n代理可用額度：${availableCredit.toLocaleString()}\n您輸入：${value.toLocaleString()}`;
                                        return Promise.reject(new Error(errorMsg));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={1}
                            placeholder={
                                selectedMember?.agent?.level === 0
                                    ? "請輸入充值金額"
                                    : selectedMember?.isDirect
                                        ? `請輸入充值金額（您的可用額度：${((selectedMember?.agent?.creditLimit || 0) - (selectedMember?.agent?.usedCredit || 0)).toLocaleString()}）`
                                        : `請輸入充值金額（代理可用額度：${((selectedMember?.agent?.creditLimit || 0) - (selectedMember?.agent?.usedCredit || 0)).toLocaleString()}）`
                            }
                            formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="note"
                        label="備註"
                    >
                        <Input.TextArea rows={3} placeholder="請輸入備註（選填）" />
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setDepositModalVisible(false)}>
                                取消
                            </Button>
                            <Button type="primary" htmlType="submit">
                                確認充值
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 提領 Modal */}
            <Modal
                title="會員提領"
                open={withdrawModalVisible}
                onCancel={() => {
                    setWithdrawModalVisible(false);
                    withdrawForm.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form form={withdrawForm} onFinish={handleWithdraw} layout="vertical">
                    <Form.Item label="會員資訊">
                        <Input
                            value={`${selectedMember?.displayName} (${selectedMember?.userId})`}
                            disabled
                        />
                    </Form.Item>

                    <Form.Item label="餘額資訊">
                        <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
                            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                                <Text>餘額: <Text strong>${(selectedMember?.wallet?.balance || 0).toLocaleString()}</Text></Text>
                            </Space>
                        </div>
                    </Form.Item>

                    {/* 新增：代理額度資訊 */}
                    {selectedMember?.agent && selectedMember.agent.level !== 0 && (
                        <Form.Item label={selectedMember?.isDirect ? "您的額度狀況" : "會員所屬代理額度"}>
                            <div style={{ background: '#fff7e6', padding: '12px', borderRadius: '6px', border: '1px solid #ffd591' }}>
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Text>{selectedMember?.isDirect ? "您的額度" : "代理"}：<Text strong>{selectedMember.agent.displayName || selectedMember.agent.username}</Text></Text>
                                    <Text>額度上限：<Text strong>${(selectedMember.agent.creditLimit || 0).toLocaleString()}</Text></Text>
                                    <Text>已用額度：<Text type="danger">${(selectedMember.agent.usedCredit || 0).toLocaleString()}</Text></Text>
                                    <Text>可用額度：<Text strong style={{ color: '#52c41a' }}>${((selectedMember.agent.creditLimit || 0) - (selectedMember.agent.usedCredit || 0)).toLocaleString()}</Text></Text>
                                    <Divider style={{ margin: '8px 0' }} />
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        💡 提領後，{selectedMember?.isDirect ? "您的" : "該代理"}已用額度會減少（可用額度增加）
                                    </Text>
                                </Space>
                            </div>
                        </Form.Item>
                    )}

                    <Form.Item
                        name="amount"
                        label="提領金額"
                        rules={[
                            { required: true, message: '請輸入提領金額' },
                            { type: 'number', min: 1, message: '金額必須大於0' },
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    const available = (selectedMember?.wallet?.balance || 0);
                                    if (value > available) {
                                        return Promise.reject(new Error(`❌ 提領金額超過會員餘額！\n會員餘額：${available.toLocaleString()}\n您輸入：${value.toLocaleString()}`));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={1}
                            placeholder={`請輸入提領金額（可提領：${(selectedMember?.wallet?.balance || 0).toLocaleString()}）`}
                            formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="note"
                        label="備註"
                    >
                        <Input.TextArea rows={3} placeholder="請輸入提領原因（選填）" />
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setWithdrawModalVisible(false)}>
                                取消
                            </Button>
                            <Button type="primary" htmlType="submit" danger>
                                確認提領
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 移除會員 Modal */}
            <Modal
                title={(
                    <span style={{ color: '#ff4d4f' }}>
                        <DeleteOutlined style={{ marginRight: 8 }} />
                        移除會員
                    </span>
                )}
                open={removeMemberModalVisible}
                onCancel={() => {
                    setRemoveMemberModalVisible(false);
                    removeMemberForm.resetFields();
                }}
                footer={null}
                width={600}
                centered
            >
                <Form
                    form={removeMemberForm}
                    onFinish={handleRemoveMember}
                    layout="vertical"
                    initialValues={{ confirmText: '' }}
                >
                    {/* 會員資訊展示 */}
                    <div style={{
                        background: '#fff2f0',
                        border: '1px solid #ffccc7',
                        borderRadius: 8,
                        padding: 16,
                        marginBottom: 20
                    }}>
                        <Title level={5} style={{ color: '#ff4d4f', marginBottom: 12 }}>
                            <DeleteOutlined style={{ marginRight: 8 }} />
                            即將移除的會員資訊
                        </Title>

                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <div>
                                <Text strong>會員ID：</Text>
                                <Text>{selectedMember?.userId}</Text>
                            </div>

                            <div>
                                <Text strong>顯示名稱：</Text>
                                <Text>{selectedMember?.displayName || '未設定'}</Text>
                            </div>

                            {selectedMember?.agent && (
                                <div>
                                    <Text strong>當前代理：</Text>
                                    <Text>{selectedMember.agent.displayName}</Text>
                                </div>
                            )}

                            {selectedMember?.wallet && (
                                <div>
                                    <Text strong>餘額資訊：</Text>
                                    <div style={{ marginLeft: 16 }}>
                                        <div>
                                            <Text>總餘額：</Text>
                                            <Text strong style={{ color: '#1890ff' }}>
                                                ${(selectedMember.wallet.balance || 0).toLocaleString()}
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {selectedMember?.totalBets && selectedMember.totalBets > 0 && (
                                <div>
                                    <Text strong>投注記錄：</Text>
                                    <Text>{selectedMember.totalBets} 筆</Text>
                                </div>
                            )}

                            {selectedMember?.lastActivity && (
                                <div>
                                    <Text strong>最後活動：</Text>
                                    <Text>{new Date(selectedMember.lastActivity).toLocaleString('zh-TW')}</Text>
                                </div>
                            )}
                        </Space>
                    </div>

                    {/* 警告提示 */}
                    <div style={{
                        background: '#fffbe6',
                        border: '1px solid #ffe58f',
                        borderRadius: 6,
                        padding: 12,
                        marginBottom: 20
                    }}>
                        <Text strong style={{ color: '#d46b08' }}>⚠️ 重要提醒：</Text>
                        <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, color: '#ad6800' }}>
                            <li>此操作將完全解除會員與您的代理關聯</li>
                            <li>移除後該會員將變為未分配狀態</li>
                            <li>會員的餘額和投注記錄將保留，但不再歸屬於您</li>
                            <li>此操作無法撤銷，請謹慎確認</li>
                        </ul>
                    </div>

                    {/* 移除原因 */}
                    <Form.Item
                        name="reason"
                        label="移除原因"
                        rules={[
                            { required: true, message: '請填寫移除原因' },
                            { min: 2, message: '移除原因至少需要2個字符' }
                        ]}
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder="請詳細說明移除此會員的原因，此信息將記錄在系統日誌中..."
                            maxLength={200}
                            showCount
                        />
                    </Form.Item>

                    {/* 確認輸入 */}
                    <Form.Item
                        name="confirmText"
                        label={(
                            <span>
                                安全確認 - 請輸入
                                <Text strong style={{ color: '#ff4d4f' }}>" 確認移除"</Text>
                                來確認此操作
                            </span>
                        )}
                        rules={[
                            { required: true, message: '請輸入確認文字' },
                            {
                                validator: (_, value) => {
                                    if (value === '確認移除') {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('請輸入正確的確認文字'));
                                }
                            }
                        ]}
                    >
                        <Input
                            placeholder="請輸入：確認移除"
                            style={{ fontFamily: 'monospace' }}
                        />
                    </Form.Item>

                    {/* 最終警告 */}
                    <div style={{
                        background: '#fff2f0',
                        border: '1px solid #ffccc7',
                        borderRadius: 6,
                        padding: 12,
                        marginBottom: 20,
                        textAlign: 'center'
                    }}>
                        <Text strong style={{ color: '#ff4d4f', fontSize: '16px' }}>
                            🚨 最後確認：此操作不可復原！
                        </Text>
                    </div>

                    {/* 按鈕組 */}
                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }} size="middle">
                            <Button
                                onClick={() => {
                                    setRemoveMemberModalVisible(false);
                                    removeMemberForm.resetFields();
                                }}
                                size="large"
                            >
                                取消
                            </Button>
                            <Button
                                type="primary"
                                danger
                                htmlType="submit"
                                size="large"
                                icon={<DeleteOutlined />}
                            >
                                確認移除會員
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 投注狀態管理 Modal */}
            <Modal
                title={
                    selectedMember?.bettingStatus === 'disabled' ||
                    bettingStatusForm.getFieldValue('bettingStatus') === 'disabled' ?
                    '禁止投注' : '恢復投注'
                }
                open={bettingStatusModalVisible}
                onCancel={() => {
                    setBettingStatusModalVisible(false);
                    bettingStatusForm.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form form={bettingStatusForm} onFinish={handleBettingStatus} layout="vertical">
                    <Form.Item label="會員資訊">
                        <Input
                            value={`${selectedMember?.displayName} (${selectedMember?.userId})`}
                            disabled
                        />
                    </Form.Item>

                    <Form.Item label="當前投注狀態">
                        <Tag color={selectedMember?.bettingStatus === 'disabled' ? 'red' : 'green'}>
                            {selectedMember?.bettingStatus === 'disabled' ? '禁止投注' : '允許投注'}
                        </Tag>
                        {selectedMember?.bettingStatus === 'disabled' && selectedMember?.bettingDisabledReason && (
                            <div style={{ marginTop: 8, fontSize: '14px', color: '#666' }}>
                                <Text type="secondary">
                                    禁投原因: {selectedMember.bettingDisabledReason}
                                </Text>
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item name="bettingStatus" style={{ display: 'none' }}>
                        <Input />
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.bettingStatus !== currentValues.bettingStatus
                        }
                    >
                        {({ getFieldValue }) => {
                            const bettingStatus = getFieldValue('bettingStatus');
                            const isDisabling = bettingStatus === 'disabled';

                            if (isDisabling) {
                                return (
                                    <div style={{
                                        background: '#fff2f0',
                                        border: '1px solid #ffccc7',
                                        borderRadius: 6,
                                        padding: 12,
                                        marginBottom: 16
                                    }}>
                                        <Text strong style={{ color: '#ff4d4f' }}>
                                            <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                                            警告：禁止投注
                                        </Text>
                                        <div style={{ marginTop: 8, fontSize: '14px', color: '#666' }}>
                                            該會員將無法進行任何投注操作，直到恢復投注權限。
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div style={{
                                    background: '#f6ffed',
                                    border: '1px solid #b7eb8f',
                                    borderRadius: 6,
                                    padding: 12,
                                    marginBottom: 16
                                }}>
                                    <Text strong style={{ color: '#52c41a' }}>
                                        <PlayCircleOutlined style={{ marginRight: 4 }} />
                                        恢復投注權限
                                    </Text>
                                    <div style={{ marginTop: 8, fontSize: '14px', color: '#666' }}>
                                        該會員將恢復正常的投注功能。
                                    </div>
                                </div>
                            );
                        }}
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.bettingStatus !== currentValues.bettingStatus
                        }
                    >
                        {({ getFieldValue }) => {
                            const bettingStatus = getFieldValue('bettingStatus');
                            const isDisabling = bettingStatus === 'disabled';

                            return (
                                <Form.Item
                                    name="reason"
                                    label="操作原因"
                                    rules={[]}
                                >
                                    <Input.TextArea
                                        rows={3}
                                        placeholder={
                                            isDisabling ?
                                            "請輸入禁止投注的原因（選填）..." :
                                            "請輸入恢復投注的原因（選填）..."
                                        }
                                        maxLength={200}
                                        showCount
                                    />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setBettingStatusModalVisible(false)}>
                                取消
                            </Button>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) =>
                                    prevValues.bettingStatus !== currentValues.bettingStatus
                                }
                            >
                                {({ getFieldValue }) => {
                                    const bettingStatus = getFieldValue('bettingStatus');
                                    const isDisabling = bettingStatus === 'disabled';

                                    return (
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            danger={isDisabling}
                                            icon={isDisabling ? <StopOutlined /> : <PlayCircleOutlined />}
                                        >
                                            {isDisabling ? '確認禁止投注' : '確認恢復投注'}
                                        </Button>
                                    );
                                }}
                            </Form.Item>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Members;